import { buildService } from './service';
import serviceMethods from './service_methods';
import { useCallback } from 'react';

const api = buildService(
  '/tracking_events',
  { only: ['create'] }
);

const isLocalhost = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
};

// Cookie helper functions
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

// Generate a UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Session ID management
const TRACKING_SESSION_COOKIE = 'tracking_session_id';

const getOrCreateSessionId = () => {
  let sessionId = getCookie(TRACKING_SESSION_COOKIE);
  
  if (!sessionId) {
    sessionId = generateUUID();
    setCookie(TRACKING_SESSION_COOKIE, sessionId);
    console.log('🆔 New tracking session created:', sessionId);
  }
  
  return sessionId;
};

const getCurrentSessionId = () => {
  return getCookie(TRACKING_SESSION_COOKIE);
};

// Track event hook
const useTrackEvent = ({ setLoading, onSuccess, onError }) => {
  const callback = useCallback(
    async (action, options = {}) => {
      if (isLocalhost()) return null;

      const sessionId = getOrCreateSessionId();
      
      const trackingData = {
        session_id: sessionId,
        action: action,
        page_url: window.location.href,
        additional_data: options.additionalData || {}
      };
      
      console.log('📊 Tracking event:', trackingData);
      
      return api.create({ tracking_event: trackingData });
    },
    []
  );

  return serviceMethods.useCreate({
    callback,
    setLoading,
    onSuccess,
    onError
  });
};

// Direct tracking function (non-hook)
const trackEvent = async (action, options = {}) => {
  try {
    if (isLocalhost()) return null;

    const sessionId = getOrCreateSessionId();
    
    const trackingData = {
      session_id: sessionId,
      action: action,
      page_url: window.location.href,
      additional_data: options.additionalData || {}
    };
    
    console.log('📊 Tracking event:', trackingData);
    
    const response = await api.create({ tracking_event: trackingData });
    
    if (options.onSuccess) {
      options.onSuccess(response);
    }
    
    return response;
  } catch (error) {
    console.error('❌ Failed to track event:', error);
    
    if (options.onError) {
      options.onError(error);
    }
    
    throw error;
  }
};

// Button click tracking utility
const trackButtonClick = (buttonName, additionalData = {}) => {
  return trackEvent(`button_click_${buttonName}`, {
    additionalData: {
      button_name: buttonName,
      ...additionalData
    }
  });
};

// Link click tracking utility
const trackLinkClick = (linkName, destination, additionalData = {}) => {
  return trackEvent(`link_click_${linkName}`, {
    additionalData: {
      link_name: linkName,
      destination: destination,
      ...additionalData
    }
  });
};

// Page view tracking
const trackPageView = (pageName, additionalData = {}) => {
  return trackEvent('page_view', {
    additionalData: {
      page_name: pageName,
      page_url: window.location.href,
      ...additionalData
    }
  });
};

// Initialize tracking on app load
const initializeTracking = () => {
  const sessionId = getOrCreateSessionId();
  console.log('🚀 Tracking initialized with session ID:', sessionId);
  
  // Track initial page view
  trackPageView('app_load');
  
  return sessionId;
};

const trackingService = {
  // Hook-based methods
  useTrackEvent,
  
  // Direct methods
  trackEvent,
  trackButtonClick,
  trackLinkClick,
  trackPageView,
  initializeTracking,
  
  // Session management
  getOrCreateSessionId,
  getCurrentSessionId,
  
  // Utility methods
  getCookie,
  setCookie
};

export default trackingService; 
